import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Boxes, Download, Pencil, Receipt, BadgeDollarSign, Plus, Trash2, X, Wallet, Check } from 'lucide-react';
import * as XLSX from 'xlsx';
import { User as UserType } from '../../types';
import apiService from '../../services/api';
import WeightToggle, { useShowWeight } from '../../components/admin/WeightToggle';
import CuentaCorrienteDetalle from './CuentaCorrienteDetalle';
import ReceiptItemsTable, { ReceiptItem, useClientPriceLists } from '../../components/admin/ReceiptItemsTable';
import { EditReceiptItemsModal, RawDetailLine } from './Recibos';
import { useAuth } from '../../contexts/AuthContext';
import { PACK_SIZE, receiptColorStyle } from '../../utils/warehouseStock';
import { bagWeightGrams, colorKeyFromLabel, formatBagKg, formatKg, parseMeasure } from '../../utils/bagWeight';
import { formatARS } from '../../utils/formatCurrency';

const formatDMY = (iso?: string) => {
  if (!iso) return '-';
  return new Date(iso.replace(' ', 'T')).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

interface ClientReceiptRow {
  id: number;
  receiptNumber: string;
  createdAt: string;
  deliveryDate?: string;
  concept: string;
  items: ReceiptItem[];
  rawItems: RawDetailLine[];
  clientId?: number | null;
  monthlyOrderId?: number | null;
}

const ClientReceiptsList: React.FC<{ clientId: number; isReadOnly: boolean }> = ({ clientId, isReadOnly }) => {
  const [receipts, setReceipts] = useState<ClientReceiptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [editingRow, setEditingRow] = useState<ClientReceiptRow | null>(null);
  const clientPrices = useClientPriceLists([clientId])[clientId];

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getReceipts({ clientId, limit: 100 });
      setReceipts(response.receipts || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudieron cargar los remitos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const downloadReceipt = async (id: number) => {
    try {
      setDownloadingId(id);
      const { blob, filename } = await apiService.downloadReceiptPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo descargar el PDF del remito.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-4">
        <h2 className="font-semibold text-gray-900">Remitos</h2>
        <WeightToggle />
      </div>
      {error && <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading ? (
        <div className="px-5 py-6 text-center text-gray-500">Cargando...</div>
      ) : receipts.length === 0 ? (
        <div className="px-5 py-6 text-center text-gray-500">Todavía no hay remitos cargados para este cliente.</div>
      ) : (
        <div className="space-y-4 p-3 sm:p-5">
          {receipts.map((r) => (
            <div key={r.id} className="overflow-hidden rounded-xl border border-gray-200 border-l-4 border-l-primary-600 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 sm:px-4">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-gray-900">{r.receiptNumber}</span>
                  <span className="text-xs text-gray-500">{formatDMY(r.deliveryDate || r.createdAt)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {!isReadOnly && (
                    <button
                      onClick={() => setEditingRow(r)}
                      className="inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      <Pencil className="h-3.5 w-3.5" />Editar
                    </button>
                  )}
                  <button
                    onClick={() => downloadReceipt(r.id)}
                    disabled={downloadingId === r.id}
                    className="inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Download className="h-3.5 w-3.5" />{downloadingId === r.id ? 'Descargando...' : 'PDF'}
                  </button>
                </div>
              </div>
              <div className="p-3 sm:p-4">
              {r.items?.length ? (
                <ReceiptItemsTable items={r.items} prices={clientPrices} />
              ) : (
                <p className="text-sm text-gray-500">{r.rawItems?.length ? r.rawItems.map((it) => (it.mode === 'libre' ? it.texto : '')).filter(Boolean).join(' · ') : r.concept}</p>
              )}
              </div>
            </div>
          ))}
        </div>
      )}
      {editingRow && (
        <EditReceiptItemsModal
          row={{ ...editingRow, clientId }}
          onClose={() => setEditingRow(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
};

interface ClientPrice { priceUpdatedAt?:string|null; id:number; widthCm:number; lengthCm:number; color:string; microns:number; costUnit:number; purchaseHasVat:boolean; saleNetUnit:number; vatRate:number; saleGrossUnit:number; marginUnit:number; unitsPerPackage:number; monthlyUnits:number; weightPerBagGrams:number; weightPerPackageKg:number; purchasePriceWithVat:number; packageCostWithoutVat:number; packageCostWithVat:number; kgCostWithoutVat:number; kgCostWithVat:number; salePricePerPackage:number; salePricePerKg:number; monthlyPurchaseWithoutVat:number; monthlyPurchaseWithVat:number; monthlySaleWithoutVat:number; monthlySaleWithVat:number; monthlyProfit:number; marginPercent:number; updatedAt:string; }
const emptyPrice = { widthCm:45, lengthCm:60, color:'negro', microns:23, costUnit:0, purchaseHasVat:false, saleNetUnit:0, vatRate:21, unitsPerPackage:50, monthlyUnits:0 };

// Tooltip of the price without VAT: when it last changed (older prices only have the last edit of the row).
const priceDateTitle=(p:ClientPrice)=>{
  const format=(value?:string|null)=>{if(!value)return '';const date=new Date(String(value).replace(' ','T')+'Z');return Number.isNaN(date.getTime())?String(value).slice(0,10):date.toLocaleString('es-AR',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'});};
  return p.priceUpdatedAt?`Precio modificado el ${format(p.priceUpdatedAt)}`:p.updatedAt?`Última edición del artículo: ${format(p.updatedAt)} (fecha del cambio de precio no registrada)`:'';
};

const ClientPriceList: React.FC<{clientId:number;clientName:string;isReadOnly:boolean;onClose?:()=>void}> = ({clientId,clientName,isReadOnly,onClose}) => {
  const showWeight = useShowWeight();
  const [prices,setPrices]=useState<ClientPrice[]>([]); const [form,setForm]=useState<any>(emptyPrice); const [editingId,setEditingId]=useState<number|null>(null); const [loading,setLoading]=useState(true); const [saving,setSaving]=useState(false); const [error,setError]=useState(''); const [adding,setAdding]=useState(false);
  const load=async()=>{try{setLoading(true);setError('');const data=await apiService.getClientPrices(clientId);setPrices(data.prices||[]);}catch(e:any){setError(e?.response?.data?.detail||'No se pudo cargar la lista de precios.');}finally{setLoading(false);}};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[clientId]);
  const reset=()=>{setForm(emptyPrice);setEditingId(null);setAdding(false);};
  const edit=(p:ClientPrice)=>{setAdding(false);setEditingId(p.id);setForm({widthCm:p.widthCm,lengthCm:p.lengthCm,color:p.color,microns:p.microns,costUnit:p.costUnit,purchaseHasVat:p.purchaseHasVat,saleNetUnit:p.saleNetUnit,vatRate:p.vatRate,unitsPerPackage:p.unitsPerPackage,monthlyUnits:p.monthlyUnits});};
  const save=async()=>{try{setSaving(true);setError('');if(editingId)await apiService.updateClientPrice(clientId,editingId,form);else await apiService.createClientPrice(clientId,form);reset();await load();}catch(e:any){setError(e?.response?.data?.detail||'No se pudo guardar el precio.');}finally{setSaving(false);}};
  const remove=async(id:number)=>{if(!window.confirm('¿Eliminar este producto de la lista de precios?'))return;try{await apiService.deleteClientPrice(clientId,id);await load();}catch(e:any){setError(e?.response?.data?.detail||'No se pudo eliminar.');}};
  const exportExcel=()=>{
    if(!prices.length)return;
    const rows=prices.map(p=>({
      Producto:`${p.widthCm} x ${p.lengthCm} cm - ${p.color} - ${p.microns} micrones`,
      'Ancho (cm)':p.widthCm,
      'Largo (cm)':p.lengthCm,
      Color:p.color,
      Micronaje:p.microns,
      'Costo unitario':p.costUnit,
      'Compra con IVA':p.purchaseHasVat?'Sí':'No',
      'Precio unitario sin IVA':p.saleNetUnit,
      'IVA (%)':p.vatRate,
      'Precio unitario final con IVA':p.saleGrossUnit,
      'Bolsas por pack':p.unitsPerPackage,
      'Precio por pack sin IVA':p.saleNetUnit*p.unitsPerPackage,
      'Precio por pack con IVA':p.salePricePerPackage,
      'Margen unitario':p.marginUnit,
      'Margen (%)':p.marginPercent,
      'Consumo mensual (bolsas)':p.monthlyUnits,
    }));
    const worksheet=XLSX.utils.json_to_sheet(rows);
    const columns=Object.keys(rows[0]);
    worksheet['!cols']=columns.map(key=>({wch:Math.min(40,Math.max(key.length,...rows.map(row=>String((row as any)[key]??'').length))+2)}));
    worksheet['!autofilter']={ref:worksheet['!ref']||`A1:${XLSX.utils.encode_col(columns.length-1)}1`};
    const moneyColumns=new Set(['Costo unitario','Precio unitario sin IVA','Precio unitario final con IVA','Precio por pack sin IVA','Precio por pack con IVA','Margen unitario']);
    columns.forEach((column,columnIndex)=>{
      if(!moneyColumns.has(column))return;
      for(let rowIndex=1;rowIndex<=rows.length;rowIndex+=1){
        const cell=worksheet[XLSX.utils.encode_cell({r:rowIndex,c:columnIndex})];
        if(cell)cell.z=column.startsWith('Precio por pack')?'$ #,##0.00':'$ #,##0.00000';
      }
    });
    const workbook=XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook,worksheet,'Lista de precios');
    const safeClientName=clientName.normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-zA-Z0-9]+/g,'-').replace(/^-|-$/g,'').toLowerCase()||String(clientId);
    XLSX.writeFile(workbook,`lista-de-precios-${safeClientName}-${new Date().toISOString().slice(0,10)}.xlsx`);
  };
  const net=Number(form.saleNetUnit||0); const gross=net*(1+Number(form.vatRate||0)/100); const margin=net-Number(form.costUnit||0); const packPrice=gross*PACK_SIZE;
  const money=(value:number,digits=5)=>`$${value.toLocaleString('es-AR',{maximumFractionDigits:digits})}`;
  const cell='w-full rounded border px-2 py-1 text-sm';
  const colorSelect=(className:string)=><select value={form.color} onChange={e=>setForm({...form,color:e.target.value})} className={className}><option value="negro">Negras</option><option value="blanco">Blancas</option><option value="azul">Azules</option><option value="verde">Verdes</option><option value="rojo">Rojas</option><option value="transparente">Transparentes</option></select>;
  const onKeyDown=(e:React.KeyboardEvent)=>{if(e.key==='Enter'&&form.saleNetUnit>0)save();if(e.key==='Escape')reset();};
  return <section className="rounded-xl border border-blue-200 bg-white shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-4 sm:px-5"><div><h2 className="flex items-center gap-2 font-semibold text-gray-900"><BadgeDollarSign className="h-5 w-5 text-primary-600"/>Lista de precios del cliente</h2><p className="text-xs text-gray-500">Valores sugeridos para ventas futuras. No modifica remitos anteriores.</p></div><div className="flex w-full items-center gap-2 sm:w-auto [&>button]:flex-1 sm:[&>button]:flex-none [&>button]:justify-center"><WeightToggle className="py-2"/>{!isReadOnly&&!adding&&<button onClick={()=>{reset();setAdding(true);}} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"><Plus className="h-4 w-4"/>Agregar producto</button>}<button onClick={exportExcel} disabled={loading||!prices.length} className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-40"><Download className="h-4 w-4"/>Exportar Excel</button>{onClose&&<button onClick={onClose} className="rounded-lg border p-2"><X className="h-4 w-4"/></button>}</div></div>
    {error&&<div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
    {!isReadOnly&&(adding||editingId)&&<div className={`m-3 rounded-lg border bg-gray-50 p-4 sm:m-5 ${adding?'':'md:hidden'}`}><h3 className="text-sm font-semibold">{adding?'Agregar producto':'Editar producto'}</h3><div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
      <label className="text-xs text-gray-600">Ancho (cm)<input type="number" value={form.widthCm} onChange={e=>setForm({...form,widthCm:Number(e.target.value)})} className="mt-1 w-full rounded border px-2 py-2 text-sm"/></label>
      <label className="text-xs text-gray-600">Largo (cm)<input type="number" value={form.lengthCm} onChange={e=>setForm({...form,lengthCm:Number(e.target.value)})} className="mt-1 w-full rounded border px-2 py-2 text-sm"/></label>
      <label className="text-xs text-gray-600">Color{colorSelect('mt-1 w-full rounded border px-2 py-2 text-sm')}</label>
      <label className="text-xs text-gray-600">Micronaje<input type="number" value={form.microns} onChange={e=>setForm({...form,microns:Number(e.target.value)})} className="mt-1 w-full rounded border px-2 py-2 text-sm"/></label>
      <label className="text-xs text-gray-600">Costo actual<input type="number" step="0.001" value={form.costUnit} onChange={e=>setForm({...form,costUnit:Number(e.target.value)})} className="mt-1 w-full rounded border px-2 py-2 text-sm"/></label>      <label className="flex items-center gap-2 self-end rounded border bg-white px-3 py-2 text-xs text-gray-700"><input type="checkbox" checked={!!form.purchaseHasVat} onChange={e=>setForm({...form,purchaseHasVat:e.target.checked})}/>La compra tiene IVA</label>
      <label className="text-xs font-medium text-blue-900">Precio sin IVA<input type="number" step="0.001" value={form.saleNetUnit} onChange={e=>setForm({...form,saleNetUnit:Number(e.target.value)})} className="mt-1 w-full rounded border border-blue-300 px-2 py-2 text-sm"/></label>
      <label className="text-xs text-gray-600">IVA %<input type="number" step="0.01" value={form.vatRate} onChange={e=>setForm({...form,vatRate:Number(e.target.value)})} className="mt-1 w-full rounded border px-2 py-2 text-sm"/></label>
      <label className="text-xs text-gray-600">Consumo mensual (bolsas)<input type="number" min="0" value={form.monthlyUnits} onChange={e=>setForm({...form,monthlyUnits:Number(e.target.value)})} className="mt-1 w-full rounded border px-2 py-2 text-sm"/></label>
    </div><div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm"><p>Final con IVA: <strong>{money(gross)}</strong> · Margen: <strong>{money(margin)}</strong>{form.costUnit>0?` (${((margin/form.costUnit)*100).toFixed(1)}%)`:''} · Precio por pack: <strong>{money(packPrice,2)}</strong> <span className="text-gray-500">({PACK_SIZE} bolsas)</span></p><div className="flex gap-2"><button onClick={reset} className="rounded-lg border px-3 py-2">Cancelar</button><button onClick={save} disabled={saving||form.saleNetUnit<=0} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white disabled:opacity-50">{adding?<Plus className="h-4 w-4"/>:<Check className="h-4 w-4"/>}{saving?'Guardando...':adding?'Agregar':'Guardar cambios'}</button></div></div></div>}
    {loading?<p className="p-5 text-gray-500">Cargando...</p>:!prices.length?<p className="p-5 text-gray-500">Todavía no agregaste productos a esta lista.</p>:<>
    <div className="divide-y md:hidden">{prices.map(p=><div key={p.id} className={`space-y-2 px-4 py-3 ${editingId===p.id?'bg-blue-50':''}`}>
      <div className="flex items-start justify-between gap-2"><div className="flex flex-wrap items-center gap-2 font-medium"><span>{p.widthCm} x {p.lengthCm} cm</span><span className="inline-flex min-w-16 justify-center rounded px-2 py-0.5 text-xs font-semibold capitalize" style={receiptColorStyle(p.color)}>{p.color}</span><span>{p.microns} μ</span></div>{!isReadOnly&&<div className="flex shrink-0 gap-2"><button onClick={()=>{edit(p);window.scrollTo({top:0,behavior:'smooth'});}} className="rounded border p-1.5" title="Editar"><Pencil className="h-4 w-4"/></button><button onClick={()=>remove(p.id)} className="rounded border border-red-200 p-1.5 text-red-600" title="Eliminar"><Trash2 className="h-4 w-4"/></button></div>}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm"><span className="text-gray-500">Final con IVA</span><span className="text-right font-semibold">{money(p.saleGrossUnit)}</span><span className="text-gray-500">Sin IVA ({p.vatRate}%)</span><span className="text-right" title={priceDateTitle(p)}>{money(p.saleNetUnit)}</span><span className="text-gray-500">Pack con IVA <span className="text-xs">({p.unitsPerPackage})</span></span><span className="text-right font-semibold text-blue-900">{money(p.salePricePerPackage,2)}</span>{showWeight&&<><span className="text-gray-500">Peso est. bolsa</span><span className="text-right"><WeightCell widthCm={p.widthCm} lengthCm={p.lengthCm} microns={p.microns} units={p.unitsPerPackage} /></span></>}<span className="text-gray-500">Costo · Margen</span><span className="text-right">{money(p.costUnit)} · {p.marginPercent}%</span></div>
    </div>)}</div>
    <div className="hidden overflow-x-auto md:block"><table className="min-w-full text-sm"><thead className="bg-gray-50 text-left text-xs uppercase text-gray-500"><tr><th className="px-4 py-3">Producto</th>{showWeight&&<th className="px-4 py-3" title="Ancho × largo × micrones × 2 × 0,92 ÷ 10.000">Peso est.</th>}<th className="px-4 py-3">Costo</th><th className="px-4 py-3">Sin IVA</th><th className="px-4 py-3">IVA</th><th className="px-4 py-3">Final con IVA</th><th className="px-4 py-3">Pack sin IVA</th><th className="px-4 py-3">Pack con IVA</th><th className="px-4 py-3">Margen</th><th className="px-4 py-3"></th></tr></thead><tbody className="divide-y">{prices.map(p=>editingId===p.id?<tr key={p.id} className="bg-blue-50 align-top" onKeyDown={onKeyDown}>
      <td className="px-4 py-2"><div className="flex items-center gap-1"><input type="number" aria-label="Ancho (cm)" value={form.widthCm} onChange={e=>setForm({...form,widthCm:Number(e.target.value)})} className={`${cell} w-16`}/><span className="text-gray-400">×</span><input type="number" aria-label="Largo (cm)" value={form.lengthCm} onChange={e=>setForm({...form,lengthCm:Number(e.target.value)})} className={`${cell} w-16`}/><span className="text-xs text-gray-500">cm</span></div><div className="mt-1 flex items-center gap-1">{colorSelect(`${cell} w-28`)}<input type="number" aria-label="Micronaje" value={form.microns} onChange={e=>setForm({...form,microns:Number(e.target.value)})} className={`${cell} w-14`}/><span className="text-xs text-gray-500">μ</span></div></td>
      {showWeight&&<td className="px-4 py-3 text-gray-600"><WeightCell widthCm={form.widthCm} lengthCm={form.lengthCm} microns={form.microns} units={form.unitsPerPackage} /></td>}
      <td className="px-4 py-2"><input type="number" step="0.001" aria-label="Costo" value={form.costUnit} onChange={e=>setForm({...form,costUnit:Number(e.target.value)})} className={`${cell} w-24`}/><label className="mt-1 flex items-center gap-1 whitespace-nowrap text-xs text-gray-600"><input type="checkbox" checked={!!form.purchaseHasVat} onChange={e=>setForm({...form,purchaseHasVat:e.target.checked})}/>Compra c/IVA</label></td>
      <td className="px-4 py-2"><input type="number" step="0.001" autoFocus aria-label="Precio sin IVA" value={form.saleNetUnit} onChange={e=>setForm({...form,saleNetUnit:Number(e.target.value)})} className={`${cell} w-28 border-blue-400 font-semibold`}/></td>
      <td className="px-4 py-2"><input type="number" step="0.01" aria-label="IVA %" value={form.vatRate} onChange={e=>setForm({...form,vatRate:Number(e.target.value)})} className={`${cell} w-16`}/></td>
      <td className="px-4 py-3 text-gray-600">{money(gross)}</td>
      <td className="px-4 py-3 text-gray-600">{money(net*PACK_SIZE,2)}<div className="mt-1 text-xs text-gray-500">{PACK_SIZE} bolsas</div></td>
      <td className="px-4 py-3 font-semibold text-blue-900">{money(packPrice,2)}</td>
      <td className="px-4 py-3">{money(margin)}{form.costUnit>0?` (${((margin/form.costUnit)*100).toFixed(1)}%)`:''}</td>
      <td className="px-4 py-2"><div className="flex gap-2"><button onClick={save} disabled={saving||form.saleNetUnit<=0} className="rounded bg-primary-600 p-1.5 text-white disabled:opacity-50" title="Guardar (Enter)"><Check className="h-4 w-4"/></button><button onClick={reset} className="rounded border p-1.5" title="Cancelar (Esc)"><X className="h-4 w-4"/></button></div></td>
    </tr>:<tr key={p.id}><td className="px-4 py-3"><div className="flex flex-wrap items-center gap-2 font-medium"><span>{p.widthCm} x {p.lengthCm} cm</span><span className="inline-flex min-w-16 justify-center rounded px-2 py-0.5 text-xs font-semibold capitalize" style={receiptColorStyle(p.color)}>{p.color}</span><span>{p.microns} μ</span></div></td>{showWeight&&<td className="px-4 py-3"><WeightCell widthCm={p.widthCm} lengthCm={p.lengthCm} microns={p.microns} units={p.unitsPerPackage} /></td>}<td className="px-4 py-3">${p.costUnit.toLocaleString('es-AR',{maximumFractionDigits:5})}</td><td className="px-4 py-3"><span title={priceDateTitle(p)} className="cursor-help underline decoration-dotted decoration-gray-300 underline-offset-4">${p.saleNetUnit.toLocaleString('es-AR',{maximumFractionDigits:5})}</span></td><td className="px-4 py-3">{p.vatRate}%</td><td className="px-4 py-3 font-semibold">${p.saleGrossUnit.toLocaleString('es-AR',{maximumFractionDigits:5})}</td><td className="px-4 py-3"><span className="font-semibold text-gray-800">${(p.saleNetUnit*p.unitsPerPackage).toLocaleString('es-AR',{maximumFractionDigits:2})}</span><span className="block whitespace-nowrap text-xs text-gray-500">{p.unitsPerPackage} bolsas</span></td><td className="px-4 py-3"><span className="font-semibold text-blue-900">${p.salePricePerPackage.toLocaleString('es-AR',{maximumFractionDigits:2})}</span><span className="block whitespace-nowrap text-xs text-gray-500">{p.unitsPerPackage} bolsas</span></td><td className="px-4 py-3">${p.marginUnit.toLocaleString('es-AR',{maximumFractionDigits:5})} ({p.marginPercent}%)</td><td className="px-4 py-3"><div className="flex gap-2">{!isReadOnly&&<><button onClick={()=>edit(p)} className="rounded border p-1.5" title="Editar"><Pencil className="h-4 w-4"/></button><button onClick={()=>remove(p.id)} className="rounded border border-red-200 p-1.5 text-red-600" title="Eliminar"><Trash2 className="h-4 w-4"/></button></>}</div></td></tr>)}</tbody></table></div></>}
  </section>;
};
const WeightCell: React.FC<{ widthCm: number; lengthCm: number; microns: number; units: number }> = ({ widthCm, lengthCm, microns, units }) => {
  const grams = bagWeightGrams(widthCm, lengthCm, microns);
  if (grams == null) return <span className="text-gray-400">-</span>;
  return <span className="whitespace-nowrap">{formatBagKg(grams)}<span className="block text-xs text-gray-500">{formatKg(grams * Number(units || 0) / 1000)} por paquete</span></span>;
};

type ClientTab = 'cuenta' | 'remitos' | 'precios';
const TABS: Array<{ key: ClientTab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { key: 'cuenta', label: 'Cuenta corriente', icon: Wallet },
  { key: 'remitos', label: 'Remitos', icon: Receipt },
  { key: 'precios', label: 'Lista de precios', icon: BadgeDollarSign },
];

const MOVEMENT_CATEGORY_LABELS: Record<string, string> = { remito: 'Remito', factura: 'Factura', cheque: 'Cheque', efectivo: 'Efectivo', transferencia: 'Transferencia', nota_credito: 'Nota de crédito', otro: 'Otro', ajuste: 'Ajuste por redondeo' };
const CHECK_STATUS_LABELS: Record<string, string> = { pendiente: 'Pendiente', depositado: 'Depositado, sin acreditar', cobrado: 'Acreditado / debitado', rechazado: 'Rechazado' };
const toDMY = (value: string) => { const [y, m, d] = String(value || '').slice(0, 10).split('-'); return d && m && y ? `${d}/${m}/${y}` : ''; };
const receiptDay = (value: string) => { const date = new Date(String(value || '').replace(' ', 'T') + 'Z'); return Number.isNaN(date.getTime()) ? toDMY(value) : date.toLocaleDateString('es-AR'); };

const appendSheet = (workbook: XLSX.WorkBook, name: string, rows: Array<Record<string, string | number>>) => {
  const data = rows.length ? rows : [{ 'Sin datos': '' }];
  const sheet = XLSX.utils.json_to_sheet(data);
  const columns = Object.keys(data[0]);
  sheet['!cols'] = columns.map((key) => ({ wch: Math.min(60, Math.max(key.length, ...data.map((row) => String(row[key] ?? '').length)) + 2) }));
  if (rows.length) sheet['!autofilter'] = { ref: sheet['!ref'] || 'A1' };
  XLSX.utils.book_append_sheet(workbook, sheet, name);
};

// One workbook with everything on the client page: current account, invoice detail, remitos and price list.
const exportClientExcel = async (client: UserType) => {
  const [{ movements }, { receipts }, { prices }] = await Promise.all([
    apiService.getAccountMovements('client', client.id),
    apiService.getReceipts({ clientId: client.id, limit: 5000 }),
    apiService.getClientPrices(client.id),
  ]);
  const round = (n: number) => Math.round(Number(n || 0) * 100) / 100;
  const workbook = XLSX.utils.book_new();

  const sorted = [...(movements || [])].sort((a: any, b: any) => String(a.movementDate).localeCompare(String(b.movementDate)) || (a.movementType === b.movementType ? a.id - b.id : a.movementType === 'documento' ? -1 : 1));
  let balance = 0;
  appendSheet(workbook, 'Cuenta corriente', sorted.map((m: any) => {
    const amount = Number(m.amount || 0);
    if (m.movementType === 'documento' || m.checkStatus !== 'rechazado') balance += m.movementType === 'documento' ? amount : -amount;
    return {
      'Fecha': toDMY(m.movementDate),
      'Tipo': m.movementType === 'documento' ? 'Documento' : 'Pago',
      'Categoría': MOVEMENT_CATEGORY_LABELS[m.category] || m.category || '',
      'N° factura': m.invoice?.number || '',
      'Debe (documentos)': m.movementType === 'documento' ? round(amount) : '',
      'Haber (pagos)': m.movementType === 'pago' ? round(amount) : '',
      'Saldo (me debe)': round(balance),
      'N° de cheque': m.checkNumber || '',
      'Banco': m.checkBank || '',
      'Emisión': toDMY(m.checkIssueDate),
      'Vencimiento': toDMY(m.checkDueDate),
      'Estado del cheque': m.category === 'cheque' ? CHECK_STATUS_LABELS[m.checkStatus] || m.checkStatus || '' : '',
      'Nota': m.note || '',
    };
  }));

  appendSheet(workbook, 'Facturas (detalle)', sorted.filter((m: any) => m.invoice?.items?.length).flatMap((m: any) => m.invoice.items.map((line: any) => ({
    'Fecha': toDMY(m.movementDate),
    'N° factura': m.invoice.number || 'sin número',
    'Artículo': line.productName,
    'Micrones': line.microns || '',
    'Packs': round(Number(line.quantity || 0) / PACK_SIZE),
    'Bolsas': Number(line.quantity || 0),
    'Pedido del mes': line.orderMonth || '',
    'Precio unit. sin IVA': round(line.unitNet),
    'IVA %': round(line.vatRate),
    'Neto': round(line.net),
    'IVA': round(line.vat),
    'Total': round(line.total),
  }))));

  const sortedReceipts = [...(receipts || [])].sort((a: any, b: any) => String(a.deliveryDate || a.createdAt).localeCompare(String(b.deliveryDate || b.createdAt)));
  appendSheet(workbook, 'Remitos', sortedReceipts.flatMap<Record<string, string | number>>((r: any) => {
    const common = { 'N° remito': r.receiptNumber, 'Fecha de entrega': r.deliveryDate ? toDMY(r.deliveryDate) : receiptDay(r.createdAt) };
    if (!r.items?.length) return [{ ...common, 'Artículo': r.concept || '', 'Color': '', 'Micrones': '', 'Packs': '', 'Bolsas': '', 'Precio unit. sin IVA': '', 'Total con IVA': '', 'Descuenta stock propio': '', 'Nota': '' }];
    return r.items.map((item: any) => {
      const bags = Number(item.quantity || 0);
      const net = Number(item.saleNetUnit || 0);
      return {
        ...common,
        'Artículo': item.productName,
        'Color': item.productColor || '',
        'Micrones': item.productMicrons || '',
        'Packs': round(bags / PACK_SIZE),
        'Bolsas': bags,
        'Precio unit. sin IVA': net ? round(net) : '',
        'Total con IVA': net ? round(net * (1 + Number(item.vatRate ?? 21) / 100) * bags) : '',
        'Descuenta stock propio': item.deduct === false ? 'No' : 'Sí',
        'Nota': item.itemNote || '',
      };
    });
  }));

  appendSheet(workbook, 'Lista de precios', (prices || []).map((price: any) => ({
    'Medida': `${price.widthCm} x ${price.lengthCm} cm`,
    'Color': price.color,
    'Micrones': price.microns || '',
    'Venta unit. sin IVA': round(price.saleNetUnit),
    'IVA %': round(price.vatRate),
    'Venta unit. final': round(price.saleGrossUnit),
    'Bolsas por pack': price.unitsPerPackage || PACK_SIZE,
    'Pack final': round(Number(price.saleGrossUnit || 0) * Number(price.unitsPerPackage || PACK_SIZE)),
  })));

  const safeName = String(client.company || client.name || 'cliente').replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]+/g, '-').replace(/^-|-$/g, '') || 'cliente';
  XLSX.writeFile(workbook, `cliente-${safeName}-${new Date().toLocaleDateString('sv-SE')}.xlsx`);
};

interface ProfitArticle { name: string; bags: number; gross: number; saleVat: number; net: number; cost: number; purchaseVat: number; profit: number; hasCost: boolean; }
interface ClientProfit { gross: number; saleVat: number; net: number; cost: number; purchaseVat: number; profit: number; articles: ProfitArticle[]; noCost: ProfitArticle[]; undetailed: number; undetailedCount: number; }

// Profit of what was invoiced: invoice net (without sale VAT) minus purchase cost without VAT.
// Purchase VAT is recovered as tax credit, so it is shown apart and never counted as cost.
const computeClientProfit = (movements: any[], prices: any[], receipts: ClientReceiptRow[]): ClientProfit => {
  const articleKey = (width: number, length: number, color: string) => `${Number(width)}x${Number(length)}|${color}`;
  const costs = new Map<string, { cost: number; purchaseVat: number }>();
  // Fallback: last purchase cost written on this client's remitos.
  [...receipts].sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt))).forEach((r) => (r.rawItems || []).forEach((raw: any) => {
    if (raw.mode !== 'bolsas' || !(Number(raw.costUnit) > 0)) return;
    costs.set(articleKey(raw.ancho, raw.largo, String(raw.color)), { cost: Number(raw.costUnit), purchaseVat: raw.purchaseHasVat ? Number(raw.costUnit) * Number(raw.vatRate ?? 21) / 100 : 0 });
  }));
  // The price list wins; several microns for one measure/color are averaged.
  const listed = new Map<string, Array<{ cost: number; purchaseVat: number }>>();
  prices.filter((price) => Number(price.costUnit) > 0).forEach((price) => {
    const key = articleKey(price.widthCm, price.lengthCm, String(price.color));
    listed.set(key, [...(listed.get(key) || []), { cost: Number(price.costUnit), purchaseVat: price.purchaseHasVat ? Number(price.costUnit) * Number(price.vatRate ?? 21) / 100 : 0 }]);
  });
  listed.forEach((list, key) => costs.set(key, { cost: list.reduce((sum, c) => sum + c.cost, 0) / list.length, purchaseVat: list.reduce((sum, c) => sum + c.purchaseVat, 0) / list.length }));

  const articles = new Map<string, ProfitArticle>();
  let undetailed = 0;
  let undetailedCount = 0;
  movements.filter((m) => m.movementType === 'documento').forEach((m) => {
    const lines = m.invoice?.items || [];
    if (!lines.length) { undetailed += Number(m.amount || 0); undetailedCount += 1; return; }
    lines.forEach((line: any) => {
      const measure = parseMeasure(line.productName);
      const color = colorKeyFromLabel(String(line.productName || '').trim().split(/\s+/).pop() || '');
      const key = measure ? articleKey(measure.widthCm, measure.lengthCm, color) : String(line.productName);
      const unit = costs.get(key);
      const bags = Number(line.quantity || 0);
      const row = articles.get(key) || { name: line.productName, bags: 0, gross: 0, saleVat: 0, net: 0, cost: 0, purchaseVat: 0, profit: 0, hasCost: !!unit };
      row.bags += bags;
      row.gross += Number(line.total || 0);
      row.saleVat += Number(line.vat || 0);
      row.net += Number(line.net || 0);
      if (unit) { row.cost += unit.cost * bags; row.purchaseVat += unit.purchaseVat * bags; }
      row.profit = row.net - row.cost;
      articles.set(key, row);
    });
  });
  const all = Array.from(articles.values()).sort((a, b) => b.profit - a.profit);
  const withCost = all.filter((a) => a.hasCost);
  const sum = (key: keyof ProfitArticle) => withCost.reduce((total, a) => total + Number(a[key]), 0);
  return { gross: sum('gross'), saleVat: sum('saleVat'), net: sum('net'), cost: sum('cost'), purchaseVat: sum('purchaseVat'), profit: sum('profit'), articles: withCost, noCost: all.filter((a) => !a.hasCost), undetailed, undetailedCount };
};

const ProfitDetail: React.FC<{ profit: ClientProfit }> = ({ profit }) => {
  const margin = profit.net ? (profit.profit / profit.net) * 100 : 0;
  const step = (label: string, value: number, tone = 'text-gray-900', sign = '') => <div className="flex justify-between gap-4 py-1"><span className="text-gray-600">{label}</span><span className={`font-semibold ${tone}`}>{sign}{formatARS(value)}</span></div>;
  return (
    <div className="mt-4 space-y-4 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 sm:p-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <div className="rounded-lg border bg-white p-3 text-sm">
          {step('Facturado con IVA', profit.gross)}
          {step('IVA venta', profit.saleVat, 'text-red-600', '− ')}
          <div className="border-t" />
          {step('Neto facturado', profit.net)}
          {step('Costo de compra sin IVA', profit.cost, 'text-red-600', '− ')}
          <div className="border-t" />
          <div className="flex justify-between gap-4 py-1.5 text-base"><span className="font-semibold">Ganancia</span><span className={`font-bold ${profit.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatARS(profit.profit)}</span></div>
          <p className="text-xs text-gray-500">Margen {margin.toLocaleString('es-AR', { maximumFractionDigits: 1 })}% sobre el neto.</p>
          {profit.purchaseVat > 0 && <p className="mt-2 text-xs text-gray-500">IVA compra {formatARS(profit.purchaseVat)}: no es costo, se recupera como crédito fiscal.</p>}
        </div>
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 text-left text-gray-500"><tr><th className="px-3 py-2">Artículo</th><th className="px-3 py-2 text-right">Packs</th><th className="px-3 py-2 text-right">Neto venta</th><th className="px-3 py-2 text-right">Costo sin IVA</th><th className="px-3 py-2 text-right">Ganancia</th><th className="px-3 py-2 text-right">Margen</th></tr></thead>
            <tbody className="divide-y">
              {profit.articles.map((a) => <tr key={a.name}><td className="px-3 py-1.5 font-medium">{a.name}</td><td className="px-3 py-1.5 text-right">{(a.bags / PACK_SIZE).toLocaleString('es-AR')}</td><td className="px-3 py-1.5 text-right">{formatARS(a.net)}</td><td className="px-3 py-1.5 text-right">{formatARS(a.cost)}</td><td className={`px-3 py-1.5 text-right font-semibold ${a.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatARS(a.profit)}</td><td className="px-3 py-1.5 text-right">{a.net ? `${((a.profit / a.net) * 100).toLocaleString('es-AR', { maximumFractionDigits: 1 })}%` : '-'}</td></tr>)}
              {!profit.articles.length && <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-500">No hay facturas con artículos y costo cargado.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {(profit.noCost.length > 0 || profit.undetailedCount > 0) && (
        <div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          {profit.noCost.length > 0 && <p><strong>Sin costo de compra, no incluidos:</strong> {profit.noCost.map((a) => `${a.name} (${(a.bags / PACK_SIZE).toLocaleString('es-AR')} packs, neto ${formatARS(a.net)})`).join(' · ')}. Cargá su costo en la Lista de precios.</p>}
          {profit.undetailedCount > 0 && <p><strong>{profit.undetailedCount} documento{profit.undetailedCount === 1 ? '' : 's'} sin artículos</strong> por {formatARS(profit.undetailed)}, no incluido{profit.undetailedCount === 1 ? '' : 's'}: cargale los artículos a la factura para sumarla.</p>}
        </div>
      )}
      <p className="text-xs text-gray-500">Se calcula sobre lo facturado hasta hoy. El costo es la "Compra unitaria sin IVA" de la lista de precios del cliente o, si el artículo no está en la lista, el último costo cargado en sus remitos.</p>
    </div>
  );
};

const ClientePagina: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRestricted = Array.isArray(user?.allowedClientIds);
  const isReadOnly = !!user?.isReadOnly;
  const id = Number(clientId);

  const stateClient = (location.state as { client?: UserType } | null)?.client;
  const [client, setClient] = useState<UserType | null>(stateClient && stateClient.id === id ? stateClient : null);
  const [loading, setLoading] = useState(!client);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') as ClientTab : 'cuenta';
  const setTab = (next: ClientTab) => setSearchParams(next === 'cuenta' ? {} : { tab: next }, { replace: true });

  const [activity, setActivity] = useState<{ movements: any[]; receipts: ClientReceiptRow[]; prices: any[] } | null>(null);
  const [showProfit, setShowProfit] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const currentYear = new Date().getFullYear();
  const exportExcel = async () => {
    if (!client) return;
    try {
      setExporting(true);
      setExportError('');
      await exportClientExcel(client);
    } catch (e: any) {
      setExportError(e?.response?.data?.detail || 'No se pudo exportar el Excel del cliente.');
    } finally {
      setExporting(false);
    }
  };

  // Se recarga al cambiar de pestaña para reflejar facturas, pagos o remitos cargados en ellas.
  useEffect(() => {
    let active = true;
    Promise.all([apiService.getAccountMovements('client', id), apiService.getReceipts({ clientId: id, limit: 5000 }), apiService.getClientPrices(id).catch(() => ({ prices: [] }))])
      .then(([account, receiptList, priceList]) => { if (active) setActivity({ movements: account.movements || [], receipts: receiptList.receipts || [], prices: priceList.prices || [] }); })
      .catch(() => { if (active) setActivity(null); });
    return () => { active = false; };
  }, [id, tab]);

  const stats = useMemo(() => {
    if (!activity) return null;
    const yearPrefix = `${currentYear}-`;
    const documents = activity.movements.filter((m) => m.movementType === 'documento');
    const yearDocuments = documents.filter((m) => String(m.movementDate).startsWith(yearPrefix));
    const payments = activity.movements.filter((m) => m.movementType === 'pago');
    return {
      total: documents.reduce((sum, m) => sum + Number(m.amount || 0), 0),
      count: documents.length,
      yearTotal: yearDocuments.reduce((sum, m) => sum + Number(m.amount || 0), 0),
      yearCount: yearDocuments.length,
      collected: payments.reduce((sum, m) => sum + Number(m.amount || 0), 0),
      lastDelivery: activity.receipts.reduce((last, r) => { const day = String(r.deliveryDate || r.createdAt || ''); return day > last ? day : last; }, ''),
    };
  }, [activity, currentYear]);
  const profit = useMemo(() => (activity ? computeClientProfit(activity.movements, activity.prices, activity.receipts) : null), [activity]);

  useEffect(() => {
    if (client) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const response = await apiService.getUsers({ role: 'cliente', page: 1, limit: 500 });
        const found = (response.users || []).find((u) => u.id === id) || null;
        if (!cancelled) {
          if (found) setClient(found);
          else setError('No se encontró el cliente.');
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.detail || 'No se pudo cargar el cliente.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return <div className="space-y-6"><p className="text-gray-600">Cargando cliente...</p></div>;
  }

  if (error || !client) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error || 'No se encontró el cliente.'}</div>
        <button onClick={() => navigate('/admin/clientes')} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"><ArrowLeft className="h-4 w-4" />Volver a clientes</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/admin/clientes')} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"><ArrowLeft className="h-4 w-4" />Volver a clientes</button>

      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">{client.company || client.name}</h1>
          <p className="break-words text-sm text-gray-600 sm:text-base">
            {client.company ? `${client.name} · ` : ''}{client.email}{client.phone ? ` · ${client.phone}` : ''}{(client.cuit || client.taxId) ? ` · CUIT ${client.cuit || client.taxId}` : ''}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap [&>a]:justify-center">
          <button onClick={exportExcel} disabled={exporting} className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
            <Download className="h-4 w-4" />{exporting ? 'Exportando...' : 'Exportar a Excel'}
          </button>
          {!isReadOnly && (
            <Link to={`/admin/remitos?clientId=${id}`} className="inline-flex items-center gap-2 rounded-lg border border-primary-600 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50">
              <Receipt className="h-4 w-4" />Cargar remito
            </Link>
          )}
          {!isRestricted && (
            <Link to={`/admin/stock-clientes?clientId=${id}`} className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <Boxes className="h-4 w-4" />Ver stock
            </Link>
          )}
        </div>
      </div>
      {exportError && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">{exportError}</div>}
      {stats && (
        <div className="mt-4 grid grid-cols-2 gap-3 border-t border-gray-100 pt-4 lg:grid-cols-3 xl:grid-cols-5">
          <div>
            <p className="text-xs text-gray-500">Ventas totales</p>
            <p className="text-lg font-bold text-gray-900 sm:text-xl">{formatARS(stats.total)}</p>
            <p className="text-xs text-gray-500">{stats.count} documento{stats.count === 1 ? '' : 's'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Ventas {currentYear}</p>
            <p className="text-lg font-bold text-gray-900 sm:text-xl">{formatARS(stats.yearTotal)}</p>
            <p className="text-xs text-gray-500">{stats.yearCount} documento{stats.yearCount === 1 ? '' : 's'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Pagos recibidos</p>
            <p className="text-lg font-bold text-green-700 sm:text-xl">{formatARS(stats.collected)}</p>
            <p className="text-xs text-gray-500">incluye cheques sin acreditar y notas de crédito</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Última entrega</p>
            <p className="text-lg font-bold text-gray-900 sm:text-xl">{stats.lastDelivery ? formatDMY(stats.lastDelivery) : '-'}</p>
          </div>
          {profit && (
            <button type="button" onClick={() => setShowProfit((v) => !v)} className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2 text-left hover:bg-emerald-50">
              <p className="text-xs text-gray-500">Ganancia al día</p>
              <p className={`text-lg font-bold sm:text-xl ${profit.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatARS(profit.profit)}</p>
              <p className="text-xs text-emerald-800">{profit.net ? `${((profit.profit / profit.net) * 100).toLocaleString('es-AR', { maximumFractionDigits: 1 })}% s/neto · ` : ''}{showProfit ? 'ocultar cálculo' : 'ver cálculo'}</p>
            </button>
          )}
        </div>
      )}
      {profit && showProfit && <ProfitDetail profit={profit} />}
      </div>

      <nav className="flex gap-1 overflow-x-auto border-b border-gray-200">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`-mb-px inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium sm:px-4 ${tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800'}`}
          >
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </nav>

      {tab === 'cuenta' && <CuentaCorrienteDetalle partyTypeOverride="client" partyIdOverride={id} embedded />}
      {tab === 'remitos' && <ClientReceiptsList clientId={id} isReadOnly={isReadOnly} />}
      {tab === 'precios' && <ClientPriceList clientId={id} clientName={client.company || client.name} isReadOnly={isReadOnly} />}
    </div>
  );
};

export default ClientePagina;
